import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Chapter, Prose, TheoryBox, MTQuote } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";

export default function Beatles10() {
  const { lesson, course, accent } = findLesson("the-beatles", "10");
  return (
    <LessonShell
      course={course}
      courseSlug="the-beatles"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <Chapter eyebrow="The workshop" title="Three songs, three different accompaniment patterns">
        <Prose>
          <p>
            The Beatles are an ideal band for accompaniment study because no two songs sound
            the same. Jason's observation: if you take one artist like Taylor Swift or Ed Sheeran,
            the accompaniment patterns will be very similar across songs. If you take the Beatles,
            the accompaniment patterns are completely different for every song. That variety makes
            them useful as a teaching toolkit.
          </p>
          <p>
            This lesson covers three songs: <strong>Twist and Shout</strong> (syncopated left-right
            hand pattern in D major), <strong>Here Comes the Sun</strong> (riff-as-melody in A major,
            with irregular groupings), and <strong>Yesterday</strong> (jazz ballad with minor
            slash chords and two-five-ones). Each song teaches a different approach to
            accompanying yourself while you sing.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Song 1" title="Twist and Shout — the syncopated pattern">
        <Prose>
          <p>
            "Twist and Shout" is in D major, using three chords: D (I), G (IV), and A (V).
            Simple harmony. The interest is entirely in the rhythm.
          </p>
          <p>
            Jason calls this a <strong>syncopated accompaniment</strong> — the pattern emulates
            a drum, with the left and right hands alternating like percussion: left, right, left,
            right, left, right, right. "The left hand plays the root and the right hand plays
            the chord." The chord in the right hand is staccato — tight, not sustained. That
            staccato makes it groovy. A sustained chord would kill the feel.
          </p>
          <p>
            Once the basic pattern is comfortable, there's an upgraded version: add a bassline
            in the left hand. The bassline just walks the roots of the chords — D, G, A, A. D,
            D, G, A, A. It starts with a pickup. This bassline version is more advanced; it
            requires coordinating the bass line with the right hand's rhythmic chord hits. Jason's
            suggestion for getting there: in the right hand, simplify to beats 2 and 4 first.
            Then layer the fuller bass line underneath.
          </p>
          <p>
            The same syncopated pattern works for "La Bamba" and many Indian folk songs. Once
            you have it, you have a lot of music.
          </p>
        </Prose>

        <YouTubeEmbed
          videoId="2RicaUqd9Hg"
          title="Twist and Shout — The Beatles"
          caption="One of the first songs that made them really famous. Count the chords: D, G, A. All the musical interest is in the syncopated rhythm pattern — the groove that drives the song."
        />

        <RomanNumeralAnalysis
          label="Twist and Shout — D major, 1-4-5"
          caption="Three chords. The pattern is the point — syncopated left-right hand hits. Left hand plays the root; right hand plays the chord. Staccato in the right hand makes it groove."
          chords={[
            { chord: "D", rn: "I", function: "Tonic" },
            { chord: "G", rn: "IV", function: "Pre-dominant" },
            { chord: "A", rn: "V", function: "Dominant" },
            { chord: "A", rn: "V", function: "Dominant" },
            { chord: "D", rn: "I", function: "Tonic" },
          ]}
        />

        <TheoryBox label="The syncopated left-right pattern">
          Basic version: left hand = root on the downbeat, right hand = chord on the off-beat.
          Pattern: L, R, L, R, L, R, R. ·{" "}
          Upgrade: add a walking bassline in the left (D, G, A, A — roots in sequence) while
          keeping the right-hand chord hits on beats 2 and 4. ·{" "}
          Key sound: staccato chords in the right hand. If you hold them, you lose the groove.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Song 2" title="Here Comes the Sun — the riff is the melody">
        <Prose>
          <p>
            "Here Comes the Sun" is in A major. What makes it different from "Twist and Shout"
            is that the melody and the riff are the same thing. Jason puts it precisely: "it's
            one of those songs where the riff is the melody or the melody is the riff." You
            cannot separate the harmonic pattern from what you're singing. They are one object.
          </p>
          <p>
            This creates a specific challenge: you have to play the melody fragment AND accompany
            it simultaneously. Jason works through a basic accompaniment pattern first — get that
            stable, then layer in the melodic riff. He adds a <strong>ghost note</strong> at the
            end of each bar, just before the chord change: "If you'd like you can add a ghost note
            at the end of the bar just prior to changing chords." The ghost note is a light touch
            on beat 4 — it anticipates the next chord and gives the bar a slight push forward.
          </p>
          <p>
            The rhythmic structure of the melody is also unusual. Jason marks it: "takita takita
            takita takita takadhimita." In counted terms: one-two-three, one-two-three, one-two-three,
            one-two-three, one-two-three-four. There are irregular groupings of three inside a
            four-four framework. The phrase doesn't divide evenly. That irregularity is part of
            what makes the melody feel unexpected — it's not where the ear expects it to land.
          </p>
          <p>
            For the E7 and B7 chords: Jason gives a shortcut. "Find the major chord shape and
            then what is a dominant 7th? It is an octave or root minus 2. And you just add that
            to the shape." For E7: find E major (E-G♯-B), then take E minus 2 semitones = D.
            Add D to the shape. That's your E7. Works for any dominant 7th.
          </p>
        </Prose>

        <YouTubeEmbed
          videoId="KQetemT1sWc"
          title="Here Comes the Sun — The Beatles"
          caption="Listen to the opening riff. That riff is also the melody — they are inseparable. The irregular groupings inside the 4/4 bar give the phrase its floating, sun-coming-up quality."
        />

        <TheoryBox label="Dominant 7th shortcut (E7, B7, any key)">
          Find the major chord. Then find: root note minus 2 semitones. Add that note to the
          chord. That note is the flat 7th (the dominant 7th interval). ·{" "}
          Example: E major = E, G♯, B. E minus 2 = D. Add D → E7 = E, G♯, B, D. ·{" "}
          Works for any dominant 7th in any key. Quick reference whenever you need to voice
          a V7 chord in an unfamiliar key.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Song 3" title="Yesterday — the jazz ballad, the slash chord, the relative minor">
        <Prose>
          <p>
            "Yesterday" ends the workshop, and it is the most harmonically rich of the three
            songs. Jason calls it "a very jazz progression kind of song because you will find
            a lot of two-five-ones in it." Jason points to the minor 2-5-1 that leads into
            D minor (the relative minor): E minor → A → D minor the first time, E minor 7♭5 → A →
            D minor the second time. Then there is one major 2-5-1 back home: G minor → C → F.
          </p>
          <p>
            The opening line contains a detail worth noticing: the chord written is D minor over C.
            That means the chord is D minor, but the bass drops to C — a slash chord. "This is
            not a C, it's a D minor over C. That means you just drop your left hand down." The
            chord stays D minor in the right hand. Only the bass changes. Jason plays it: D minor
            in the right hand, left hand moves down one note to C. This is the sound of that first
            line.
          </p>
          <p>
            There are also dotted rhythms in "Yesterday" — "a quarter and a dotted minim" appears
            in one bar. The chord changes irregularly: some chords last a minim, some get a dotted
            minim. Jason notates it carefully: whenever chords are irregular, he writes the beat
            count above to make the timing clear.
          </p>
          <p>
            The overall observation: "Amazing how he keeps going from the major to the relative
            minor in this song. Best example of a song which is composed major-minor." F major and
            D minor live side by side throughout "Yesterday" — the song never fully commits to
            either. That ambiguity is what gives it its emotional complexity. It's neither happy
            nor sad. It's both.
          </p>
          <p>
            The accompaniment: ballad style. "Simple ballad would be four in the right and a
            single one in the left. But then here and there you can spice it up by adding either
            arpeggios in the right or by playing off beats." Start simple. Spice later.
          </p>
        </Prose>

        <YouTubeEmbed
          videoId="NrgmdOz227I"
          title="Yesterday — The Beatles"
          caption="Listen for the major-to-relative-minor movement throughout. F major and D minor keep exchanging — the song never settles fully into either. The D minor over C slash chord in the opening line is the harmonic detail to find."
        />

        <RomanNumeralAnalysis
          label="Yesterday — F major with frequent relative minor"
          caption="A minor 2-5-1 into D minor (E minor 7♭5 → A7 → D minor), then the D minor over C slash chord (Dm/C), which drops the bass without changing the upper chord. 'Best example of a song which is composed major-minor.' — Jason"
          chords={[
            { chord: "F", rn: "I", function: "Tonic" },
            { chord: "Em7b5", rn: "iiø7/vi", function: "Pre-dominant" },
            { chord: "A7", rn: "V7/vi", function: "Secondary V" },
            { chord: "Dm", rn: "vi", function: "Tonic" },
            { chord: "Dm/C", rn: "vi⁴₂", function: "Tonic" },
            { chord: "Bb", rn: "IV", function: "Pre-dominant" },
            { chord: "C7", rn: "V7", function: "Dominant" },
            { chord: "F", rn: "I", function: "Tonic" },
          ]}
        />

        <MTQuote>
          "If you take the Beatles, the accompaniment patterns are very different for every song."
          — Jason on why the Beatles are the ideal study band. Three songs, three completely
          different techniques. That's the point.
        </MTQuote>
      </Chapter>
    </LessonShell>
  );
}
